import { z } from 'zod';
import { Paths } from 'type-fest';

import {
  Audience_Operator,
  Point_Balance_Target,
  ProductCategoryFragment,
  Time_Since_Membership_Acquisition_Operator,
} from '@occc/graphql';

import { isNil } from 'lodash';
import { MembershipCreatedValues, PointBalanceValues } from './types';
import { ProductInterestsControl, ProductInterestsMatch } from '../types';
import { addCustomIssue as addIssue } from '../utils';

export const productInterestsSchema = z.object({
  prefer: z.nativeEnum(ProductInterestsControl),
  match: z.nativeEnum(ProductInterestsMatch),
  productCategories: z.array(z.custom<ProductCategoryFragment>()).optional(),
});

export type ProductInterestsValues = z.infer<typeof productInterestsSchema>;

export const membershipCreatedSchema = z
  .object({
    operator: z
      .union([z.nativeEnum(Time_Since_Membership_Acquisition_Operator), z.literal('')])
      .optional(),
    amount: z.number().min(0).nullable().optional(),
  })
  .superRefine((data, ctx) => {
    const { operator, amount } = data;
    const hasOperator = !!operator;
    const hasAmount = typeof amount === 'number';

    if (hasAmount && !hasOperator) {
      addIssue(ctx, 'operator' satisfies Paths<MembershipCreatedValues>);
    }
    if (hasOperator && !hasAmount) {
      addIssue(ctx, 'amount' satisfies Paths<MembershipCreatedValues>);
    }
  })
  .optional();

export const pointBalanceOperatorSchema = z
  .union([z.nativeEnum(Audience_Operator), z.literal('BETWEEN'), z.literal('')])
  .optional();

export const pointBalanceSchema = z
  .object({
    operator: pointBalanceOperatorSchema,
    points: z.number().min(0).nullable().optional(),
    toPoints: z.number().nullable().optional(),
    period: z.union([z.nativeEnum(Point_Balance_Target), z.literal('')]).optional(),
  })
  .superRefine((data, ctx) => {
    const { operator, points, toPoints, period } = data;
    const hasOperator = !!operator;
    const hasPoints = typeof points === 'number';
    const hasPeriod = !!period;
    const isAnyFilled = hasOperator || hasPoints || hasPeriod;
    const isAllFilled = hasOperator && hasPoints && hasPeriod;

    if (isAnyFilled && !isAllFilled) {
      if (!hasOperator) addIssue(ctx, 'operator' satisfies Paths<PointBalanceValues>);
      if (!hasPoints) addIssue(ctx, 'points' satisfies Paths<PointBalanceValues>);
      if (!hasPeriod) addIssue(ctx, 'period' satisfies Paths<PointBalanceValues>);
    }

    if (operator === 'BETWEEN') {
      if (isNil(toPoints)) addIssue(ctx, 'toPoints' satisfies Paths<PointBalanceValues>);
      else if (hasPoints && toPoints <= points!) {
        addIssue(ctx, 'toPoints', 'The ending value must be greater than the starting value');
      }
    }
  })
  .optional();
